import {
  Box,
  Radio,
  RadioGroup,
  Typography,
  FormControlLabel,
} from "@mui/material";
import { queryClient } from "@/queryClient";
import { GetLeadResponse } from "@/modules/leads/dtos";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import FeedBackModal from "@/modules/common/components/modals/feedback-modal";
import useMergeLead from "@/modules/lead-page/hooks/lead-info/lead-actions/merge-lead.hook";
import LeadInputSection from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/components/merge-lead-selector";
import LeadTypeContent from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/components/merge-lead-type";
import ConfirmMergeModal from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/modals/confirm-merge-modal";
import MergeLeadsSkeleton from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/skeletons/merge-leads-skeleton";
import SalesSection from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/components/sales-section";
import MergeDisclaimer from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/components/merge-disclaimer";

const MergeLeadForm = ({
  currentParentLead,
  setDisabledConfirmButton,
  setSubmitMergeLead,
  submitMergeLead,
  openConfirmMergeModal,
  setOpenConfirmMergeModal,
  setMergeLeadModalOpen,
}: {
  currentParentLead: GetLeadResponse | undefined;
  setDisabledConfirmButton: (boolean: boolean) => void;
  setSubmitMergeLead: (boolean: boolean) => void;
  setOpenConfirmMergeModal: (boolean: boolean) => void;
  setMergeLeadModalOpen: (boolean: boolean) => void;
  submitMergeLead: boolean;
  openConfirmMergeModal: boolean;
}) => {
  const {
    handleParentLeadChange,
    handleChildLeadChange,
    setIsFeedBackModalOpen,
    setSelectedName,
    handleSelectionChange,
    handleMergeLeads,
    isFeedBackModalOpen,
    feedBackMsg,
    feedBackTitle,
    isSameLeadIdError,
    selectedName,
    selections,
    isParentLoading,
    isChildFetching,
    isParentError,
    isChildError,
    isMergeLeadLoading,
    searchChildLeadId,
    searchParentLeadId,
    parentLeadData,
    childLeadData,
    isMergeLeadError,
    sortedLeadTypeGroups,
    parentLeadSales,
    childLeadSales,
    childPropertyTracksInfo,
  } = useMergeLead({
    openConfirmMergeModal,
    currentParentLead,
    submitMergeLead,
    setSubmitMergeLead,
    setOpenConfirmMergeModal,
    setDisabledConfirmButton,
  });
  const hasPropertyTracks = childPropertyTracksInfo?.hasAnyTracks ?? false;

  return (
    <>
      <LeadInputSection
        searchParentLeadId={searchParentLeadId}
        searchChildLeadId={searchChildLeadId}
        handleParentLeadChange={handleParentLeadChange}
        handleChildLeadChange={handleChildLeadChange}
      />

      {isParentLoading || isChildFetching ? (
        <MergeLeadsSkeleton />
      ) : (
        (parentLeadData || childLeadData) && (
          <>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: nawyTheme.nawy.spacing.lg,
              }}
            >
              <Box
                sx={{
                  gap: 2,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  mt: nawyTheme.nawy.spacing.lg,
                }}
              >
                <Typography
                  sx={{
                    ...nawyTheme.nawy.typography.english.variants.body.xs
                      .semibold,
                    color: nawyTheme.nawy.colors.semantic.text.secondary,
                  }}
                >
                  Name
                </Typography>

                <RadioGroup
                  row
                  value={selectedName}
                  onChange={(e) => setSelectedName(e.target.value)}
                  sx={{ height: 20, width: "100%", alignItems: "center" }}
                >
                  {[
                    parentLeadData?.data.lead?.name,
                    childLeadData?.data.lead?.name,
                  ].map((name, index) => (
                    <Box
                      key={index}
                      sx={{
                        gap: 2,
                        width: "50%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FormControlLabel
                        value={name}
                        control={<Radio sx={{ p: 0, mr: 1 }} />}
                        label={
                          <Typography
                            sx={{
                              ...nawyTheme.nawy.typography.english.variants.body
                                .md.regular,
                              color:
                                nawyTheme.nawy.colors.semantic.text.primary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {name ? name.toLowerCase() : "not set"}
                          </Typography>
                        }
                        sx={{ m: 0, width: "100%" }}
                      />
                    </Box>
                  ))}
                </RadioGroup>
              </Box>

              {sortedLeadTypeGroups.map((group) => (
                <Box>
                  {" "}
                  <LeadTypeContent
                    group={group}
                    selections={selections}
                    handleSelectionChange={handleSelectionChange}
                  />
                  {/* {((group.parent?.listings?.length ?? 0) > 0 ||
                    (group.child?.listings?.length ?? 0) > 0) && (
                    <MergedListings group={group} />
                  )} */}
                </Box>
              ))}
            </Box>

            {childLeadData && (
              <MergeDisclaimer
                showUnifiedContact={true}
                showPropertyTracksNote={hasPropertyTracks}
              />
            )}

            {/* Sales Section */}
            {(parentLeadSales?.data || childLeadSales?.data) && (
              <SalesSection
                parentSales={parentLeadSales?.data}
                childSales={childLeadSales?.data}
              />
            )}
          </>
        )
      )}

      <ConfirmMergeModal
        open={openConfirmMergeModal}
        onClose={() => setOpenConfirmMergeModal(false)}
        onConfirm={handleMergeLeads}
        isLoading={isMergeLeadLoading}
        selections={selections}
        parentLeadData={parentLeadData}
        sortedLeadTypeGroups={sortedLeadTypeGroups}
        selectedName={selectedName}
        parentSales={parentLeadSales?.data}
        childSales={childLeadSales?.data}
        hasPropertyTracks={hasPropertyTracks}
      />

      <FeedBackModal
        open={isFeedBackModalOpen}
        setOpen={setIsFeedBackModalOpen}
        handleOnClickOk={() => {
          if (
            isParentError ||
            isChildError ||
            isSameLeadIdError ||
            isMergeLeadError
          ) {
            setIsFeedBackModalOpen(false);
          } else {
            setOpenConfirmMergeModal(false);
            setIsFeedBackModalOpen(false);
            setMergeLeadModalOpen(false);
            queryClient.removeQueries(["getChildLead"]);
            queryClient.invalidateQueries({
              queryKey: ["getLead"],
              refetchType: "active",
            });
            queryClient.invalidateQueries({ queryKey: ["getLeadTracks"] });
          }
        }}
        failure={
          isParentError ||
          isChildError ||
          isSameLeadIdError ||
          isMergeLeadError ||
          feedBackTitle === "Cannot Merge Leads"
        }
        title={feedBackTitle}
        message={feedBackMsg}
      />
    </>
  );
};

export default MergeLeadForm;
